import { useState } from "react";
import { Link } from "react-router-dom";
import LegalLayout from "./legal/LegalLayout";

const SECTIONS = [
  {
    title: "Plans & Pricing",
    items: [
      {
        q: "What's the difference between Establishment and Enterprise?",
        a: (
          <>
            <p><strong>Establishment — $179/month</strong> is a flat rate for a single location. It's the right choice for individual restaurants, cafés, and small retailers who operate out of one place.</p>
            <p><strong>Enterprise — $99/month per location</strong> is built for two or more locations. Each location gets its own address, phone, hours, and tax settings, all managed from one dashboard. Call and transaction meters are shared across the account.</p>
          </>
        ),
      },
      {
        q: "Can I switch plans?",
        a: <p>Yes. Upgrade from Establishment to Enterprise by adding a second location in your Locations tab. To downgrade, reach out to <a href="mailto:support@ordrs.ai">support@ordrs.ai</a>.</p>,
      },
      {
        q: "When does my billing cycle renew?",
        a: <p>Monthly, on the same calendar date you went live. If you activated on June 15th, you're billed on the 15th of every month going forward.</p>,
      },
      {
        q: "Are there any hidden fees?",
        a: <p>No. You pay subscription + calls + transactions, plus the one-time setup fee. The only other charges are optional add-ons you turn on yourself: a dedicated AI phone number ($2/month) and SMS marketing ($9/month). No per-API-call charges, no overage penalties, no surprises on your invoice.</p>,
      },
      {
        q: "What happens when I click Go live?",
        a: (
          <>
            <p>You're taken to your first invoice — first month's subscription plus the setup fee (or first installment). Pay it with any card and your subscription activates immediately: the preview banner comes off, checkout unlocks, and your storefront starts accepting real orders.</p>
            <p>The card you use is saved as your subscription's payment method, so renewals charge automatically each month.</p>
          </>
        ),
      },
      {
        q: "How do I cancel?",
        a: <p>Email <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> and we'll cancel your subscription effective at the end of your current billing cycle. Your storefront reverts to preview mode. If you're on the setup installment plan, the remaining installment balance is due at cancellation.</p>,
      },
    ],
  },
  {
    title: "Calls & Transactions",
    items: [
      {
        q: "How does call billing work?",
        a: (
          <>
            <p>Calls are billed at <strong>$0.10 each</strong> when your AI host takes an order or booking on the call. Usage accumulates during your billing period and appears as a single line item on your next invoice.</p>
            <p className="text-muted-foreground text-sm">Example: 100 order-taking calls in June = $10 on your next invoice. Test calls from the Phone Simulator use build credits instead — they never hit the call meter.</p>
          </>
        ),
      },
      {
        q: "How does transaction billing work?",
        a: (
          <>
            <p>You choose one fee model at go-live and it applies to every order paid through Ordrs. Either way, order payments settle to your Stripe account in full — the fee is billed once, on your monthly invoice, never deducted from the charge itself.</p>
            <p><strong>$1.50 Convenience Fee (Recommended)</strong> — the fee appears as a line item on the customer's receipt, so the customer covers it. You receive it with the order payment, and your monthly invoice bills $1.50 per paid order — net-zero overhead for you.</p>
            <p><strong>1% Platform Fee</strong> — no extra charge to customers. Your monthly invoice automatically bills 1% of each paid order's subtotal. Cleaner checkout experience; you absorb the fee.</p>
          </>
        ),
      },
      {
        q: "Can I switch between fee models?",
        a: <p>Yes, in your <strong>Settings</strong> tab. Changes apply at the start of your next billing cycle — not immediately. If your cycle renews on the 15th and you switch mid-month, the new model kicks in on the 15th.</p>,
      },
    ],
  },
  {
    title: "Setup Fee",
    items: [
      {
        q: "What is the setup fee?",
        a: <p>A one-time <strong>$899 activation fee</strong> that covers onboarding, Stripe integration, and merchant enablement. It's charged once — never again.</p>,
      },
      {
        q: "Do I have to pay it all upfront?",
        a: (
          <>
            <p>No. You can split it into <strong>6 monthly installments of $150</strong> ($900 total). Your account activates immediately either way.</p>
            <p><strong>Heads up:</strong> If you cancel while on the installment plan, the remaining balance is charged immediately.</p>
          </>
        ),
      },
      {
        q: "Is there a money-back guarantee?",
        a: <p>Yes. If you're not satisfied within the first 7 days, contact <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> to discuss refund eligibility on setup fees.</p>,
      },
    ],
  },
  {
    title: "Invoices & Payments",
    items: [
      {
        q: "Where do I find my invoices?",
        a: (
          <>
            <p>All invoices live in your <strong>Stripe dashboard</strong> at <a href="https://dashboard.stripe.com" target="_blank" rel="noreferrer">dashboard.stripe.com</a> → Billing → Invoices. You'll see itemized charges for subscription, calls, transactions, and setup fee (if applicable).</p>
          </>
        ),
      },
      {
        q: "What happens if a payment fails?",
        a: <p>Stripe retries automatically over several days. If it continues to fail, your account may be suspended. Update your payment method in Stripe under Settings → Payment methods, or contact <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> for help.</p>,
      },
      {
        q: "What payment methods are accepted?",
        a: <p>Any method on file in your Stripe account — credit cards, bank accounts, and more. Stripe handles all payment processing directly.</p>,
      },
    ],
  },
  {
    title: "Storefront & AI Generation",
    items: [
      {
        q: "Do I need an existing website to build a storefront?",
        a: (
          <>
            <p>No. Give us anything to work with — paste your menu as text, upload a photo or PDF of it, or just your business name and type. The builder generates a complete storefront from whatever you provide.</p>
            <p>If you <em>do</em> have a website, drop the URL in and we'll scan it: your menu, brand colors, and imagery are pulled automatically so the result matches the brand you already have.</p>
          </>
        ),
      },
      {
        q: "I already have a website — can I keep it and just add ordering?",
        a: (
          <>
            <p>Yes. In the builder, choose <strong>"Use my existing site"</strong> instead of building a new storefront. You'll still enter your business type (orders or appointments) and your website URL — we scan that URL to build your priced Order page and pull your brand colors/fonts so checkout matches your existing site.</p>
            <p>In this mode we only generate the <strong>Order</strong> page. Your Home, Menu, and About pages stay exactly as they are on your current site — we never touch them, and your existing host keeps serving them unchanged.</p>
            <p>You'll connect a <strong>subdomain</strong> (e.g. <code>order.yourbusiness.com</code>) to point at your Ordrs checkout — not your root domain, so your main site's hosting is never at risk. See the Custom Domain section below for the DNS record.</p>
            <p>If the auto-detected colors or fonts don't quite match, you can adjust them anytime in your dashboard's Storefront tab.</p>
          </>
        ),
      },
      {
        q: "Is Ordrs only for restaurants?",
        a: (
          <>
            <p>No. The builder is tuned for restaurants, bakeries, cafés, salons, spas, barbershops, nail studios, florists, retail boutiques, workshops, and juice bars — each vertical gets its own imagery, layout, and palette direction so a spa never looks like a steakhouse.</p>
            <p>Service businesses pair naturally with the <strong>Booking Host</strong> phone persona and appointments booking mode; retail and food businesses use the standard ordering flow.</p>
          </>
        ),
      },
      {
        q: "How many free refinements do I get?",
        a: <p>Your initial generation is always free, plus <strong>one free refinement</strong> — enough to try a new direction before committing. After that, credits are $5 for 10 ($0.50 each). Storefront refinements and phone simulator calls draw from the same pool.</p>,
      },
      {
        q: "What can a refinement actually change?",
        a: <p>Describe the change in plain language and the AI applies it holistically: colors, fonts, layout style, page copy, and brand voice all move together ("go darker and more luxurious", "make it feel like a Tokyo listening bar"). Refinements can also <strong>add menu items</strong> — "add a cheesecake at $9" creates the item on your live menu and storefront in one step.</p>,
      },
      {
        q: "Can I edit my storefront after going live?",
        a: <p>Yes. The <strong>Storefront tab</strong> in your dashboard lets you edit colors, fonts, and page copy directly — no credits needed for simple changes. For bigger vibe shifts, the full AI builder is accessible from your dashboard too.</p>,
      },
      {
        q: "What pages does my storefront include?",
        a: (
          <>
            <p>If you build a new storefront, you get four pages: <strong>Home</strong> (brand hero + story), <strong>Menu</strong> (a taste of what you offer — vibe, not a price list), <strong>Story</strong> (your background and what makes you different), and <strong>Order</strong> (the functional ordering page where customers browse, add to cart, and pay). The AI composes Home/Menu/Story freshly for your business rather than reusing the same layout for every account, so no two storefronts are structured identically.</p>
            <p>If you chose to keep your existing website instead, you only get the <strong>Order</strong> page — see "I already have a website" above.</p>
            <p>Customers flow from Menu (atmosphere) → Order (action). The cart is always visible in the top nav once items are added.</p>
          </>
        ),
      },
      {
        q: "The storefront builder remembered me — how?",
        a: <p>A 30-day browser cookie stores your business name and storefront link. When you return to ordrs.ai, you'll see a "Welcome back" banner with a direct link to resume your build. Click × to start fresh.</p>,
      },
      {
        q: "What does the PREVIEW MODE banner mean?",
        a: (
          <>
            <p>Every storefront starts in preview: fully browsable, but checkout is disabled and no real payments can happen. It's your risk-free sandbox to refine the design and menu.</p>
            <p>Going live (plan + setup fee) removes the banner and unlocks real ordering. If you just want more time to build before committing, a one-time <strong>$10 preview unlock</strong> extends your preview and keeps your work in place.</p>
          </>
        ),
      },
      {
        q: "I built a storefront before signing up — how do I keep it?",
        a: <p>Create an account (or log in) and claim the build from your preview page. Claiming ties the storefront to your account so only you can edit it; unclaimed previews remain open to whoever holds the link.</p>,
      },
    ],
  },
  {
    title: "AI Phone Agent",
    items: [
      {
        q: "What does the AI phone agent do?",
        a: <p>It answers your business phone, takes orders or bookings, and can send a follow-up payment or confirmation link — 24/7, no staff required. It knows your menu or service list, handles common modifications, and recognizes returning callers.</p>,
      },
      {
        q: "Do I need a new phone number?",
        a: <p>No. Phone setup is handled for you. If you want a dedicated Twilio number, you can enable it in settings. Otherwise, you can keep your existing number and route calls the way you prefer.</p>,
      },
      {
        q: "Can I test it before going live?",
        a: <p>Yes. During the build, a "Test a call" button opens the AI Phone Simulator — a full end-to-end test against your real menu. Your first test is free; additional tests use credits from the same pool as storefront refinements.</p>,
      },
      {
        q: "Does the agent disclose it's AI?",
        a: <p>You control this. An AI-and-recording disclosure can be turned on in <strong>AI Host → AI &amp; recording disclosure</strong>, with your own wording or a built-in default; it plays at the start of every call once enabled. It's off by default. Some states (California, Florida, Illinois, and others) require callers be told they're speaking with an AI and/or that the call may be recorded — you're responsible for determining what applies to your business and enabling it accordingly.</p>,
      },
      {
        q: "What languages does the phone agent support?",
        a: <p>The agent's voice language can be set in <strong>AI Host → AI voice language</strong>. Supported: English, Spanish, French, German, Hindi, Japanese, Korean, Portuguese, and Mandarin Chinese. Speech recognition (Deepgram Nova-3) auto-detects whatever language the caller speaks.</p>,
      },
      {
        q: "What are the AI phone host personas?",
        a: (
          <>
            <p>The persona controls how your AI host presents itself on calls. You can set it in the <strong>AI Host</strong> tab alongside your other phone settings. There are three options:</p>
            <p><strong>Order Host</strong> — the default. Takes phone orders and can handle reservations. Best for restaurants, bakeries, florists, and retail.</p>
            <p><strong>Booking Host</strong> — books appointments and manages your service calendar. Best for salons, clinics, spas, therapists, and studios.</p>
            <p><strong>Concierge</strong> — a polished, full-service tone that handles orders, bookings, and guest requests. Best for hospitality, premium services, and high-touch businesses.</p>
            <p>The persona is independent from your booking mode toggle — you can set each to match your business without them affecting each other.</p>
          </>
        ),
      },
      {
        q: "What happens when someone calls after hours?",
        a: (
          <>
            <p>It depends on your <strong>After-hours calls</strong> toggle in the AI Host tab.</p>
            <p><strong>On (default)</strong> — your AI host answers 24/7. After hours, it acknowledges the time, takes orders for future pickup or delivery, answers FAQ about your business, and acts as a proactive salesperson. No call goes to waste.</p>
            <p><strong>Off</strong> — callers hear a voicemail message and can leave a recording. The AI session never starts, so no call charges accumulate while you're closed.</p>
            <p>Your business hours are set in <strong>Settings → Business profile</strong> — the same hours feed your storefront and your AI host.</p>
          </>
        ),
      },
      {
        q: "Does the AI host know about my business beyond the menu?",
        a: <p>Yes. The AI host reads your <strong>business hours</strong>, <strong>about / story text</strong>, and your full live menu or service list on every call. Set these in <strong>Settings → Business profile</strong>. When a caller asks "what are you known for?" or "when do you close?", the host answers from those fields — not a generic script. Keep them up to date and the host stays accurate automatically.</p>,
      },
    ],
  },
  {
    title: "Payments & Stripe",
    items: [
      {
        q: "Do I need a Stripe account?",
        a: <p>Yes. Ordrs routes customer payments directly to your Stripe account via Stripe Connect. You get paid directly — we never hold your money.</p>,
      },
      {
        q: "When do I get paid?",
        a: <p>Stripe's standard payout schedule applies to your connected account — typically 2 business days after a payment clears. This is independent of your Ordrs subscription billing.</p>,
      },
      {
        q: "How are refunds handled?",
        a: <p>Ordrs manages refunds end-to-end. When a refund is issued, the order is marked Refunded in your dashboard and the associated platform fee is voided — you're never charged a transaction fee on returned money.</p>,
      },
    ],
  },
  {
    title: "Custom Domain",
    items: [
      {
        q: "Do I need to buy a domain through Ordrs?",
        a: <p>No. You bring your own domain from any registrar (GoDaddy, Namecheap, Cloudflare, etc.). We never hold your domain — you're only adding one DNS record that points a subdomain at our servers.</p>,
      },
      {
        q: "I chose to keep my existing website — can I connect my root domain (e.g. yourbusiness.com)?",
        a: <p>No — only a subdomain (e.g. <code>order.yourbusiness.com</code>). If you kept your existing site, your root domain needs to keep pointing at your current host so your site keeps working. Connecting a subdomain instead means your main site is completely unaffected; only that one subdomain routes to your Ordrs checkout.</p>,
      },
      {
        q: "What DNS record do I add?",
        a: (
          <>
            <p>For a subdomain like <code>orders.yourbusiness.com</code>:</p>
            <pre className="bg-muted px-4 py-3 rounded text-sm my-3 overflow-x-auto">Type: CNAME{"\n"}Name: orders{"\n"}Value: cname.vercel-dns.com</pre>
            <p>Log into your DNS provider, add that record, and you're done.</p>
          </>
        ),
      },
      {
        q: "How long does it take to go live?",
        a: <p>SSL is usually provisioned within 5–15 minutes after your DNS record is saved. Full propagation can take up to an hour, occasionally longer depending on your provider's TTL settings.</p>,
      },
      {
        q: "My domain isn't loading. What do I check?",
        a: (
          <ol className="list-decimal pl-5 space-y-1">
            <li>Confirm the CNAME record is saved correctly in your DNS provider.</li>
            <li>Wait 15–60 minutes for propagation and try again.</li>
            <li>Make sure the domain in your dashboard exactly matches your DNS record (e.g. <code>orders.yourbusiness.com</code>, not <code>www.orders.yourbusiness.com</code>).</li>
            <li>Enter the bare domain — no <code>https://</code> prefix — in the dashboard.</li>
            <li>Still stuck? Email <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> with your domain and we'll diagnose it.</li>
          </ol>
        ),
      },
    ],
  },
  {
    title: "Customers & Marketing",
    items: [
      {
        q: "Where do I see my customers?",
        a: <p>The <strong>Customers tab</strong> in your dashboard shows every unique customer derived from your order history — name, phone, order count, lifetime spend, and last order date. Customers are identified by phone number and appear automatically after their first order. No manual entry required.</p>,
      },
      {
        q: "How do I send an SMS to my customers?",
        a: (
          <>
            <p>Enable <strong>SMS marketing</strong> in the Customers tab, then use the compose area to write a message (up to 160 characters) and send to your entire customer list in one tap. A confirmation dialog shows the recipient count before anything goes out.</p>
            <p>Requires an AI phone number provisioned in the AI Host tab — messages send from that number. The $9/mo add-on only charges when you actually send.</p>
          </>
        ),
      },
      {
        q: "Can I target specific customers with SMS, or is it everyone?",
        a: <p>The current SMS blast sends to all customers who have placed at least one order. Segmented sends (by spend tier, last order date, loyalty status, etc.) are on the roadmap.</p>,
      },
      {
        q: "Are customers opted in to SMS marketing by default?",
        a: <p>Customers in your list have provided their phone number as part of placing an order with your business. It is your responsibility as the merchant to ensure you have appropriate consent before sending marketing messages. Best practice is to inform customers at the point of sale that they may receive occasional texts from you.</p>,
      },
    ],
  },
  {
    title: "Delivery & Order Types",
    items: [
      {
        q: "Does Ordrs support delivery?",
        a: (
          <>
            <p>Yes. You can enable delivery in <strong>Settings → Checkout → Order type</strong>. When enabled, customers see a Pickup / Delivery selector at checkout and must enter a delivery address before placing their order.</p>
            <p>Ordrs does not dispatch or coordinate couriers — you manage your own delivery (in-house staff, a contracted driver, or any third-party service you already use). Ordrs simply captures the order and address and routes it to your fulfillment queue.</p>
          </>
        ),
      },
      {
        q: "How do I set a delivery fee?",
        a: <p>After enabling delivery in <strong>Settings → Checkout → Order type</strong>, a delivery fee input appears. Enter a flat dollar amount — this fee is added to the customer's total at checkout and shown as a line item on their receipt. Set it to $0 for free delivery.</p>,
      },
      {
        q: "Can I offer both pickup and delivery at the same time?",
        a: <p>Yes. When both Pickup and Delivery are enabled, customers see a two-button selector on the order page and choose their preference. If only one mode is enabled, checkout defaults to that mode with no selector shown.</p>,
      },
      {
        q: "Can I disable pickup and only offer delivery?",
        a: <p>Yes. Toggle off Pickup in <strong>Settings → Checkout → Order type</strong> and leave Delivery on. Customers will proceed directly to delivery checkout with no pickup option shown.</p>,
      },
      {
        q: "Does delivery affect how the order appears in the fulfillment queue?",
        a: <p>Yes. Delivery orders are tagged "Delivery" in your fulfillment queue and include the customer's address so staff or drivers know where the order is going. Pickup orders show "Pickup" as before.</p>,
      },
    ],
  },
  {
    title: "Dashboard Features",
    items: [
      {
        q: "What is the fulfillment queue?",
        a: <p>A real-time board showing incoming orders grouped by status: Pending → Preparing → Ready → Completed. Staff use it to track and advance orders without paper tickets. Access it from the Fulfillment tab or open the full-screen view at <code>/fulfillment/your-slug</code> on a dedicated tablet or monitor.</p>,
      },
      {
        q: "How does the loyalty program work?",
        a: <p>When enabled (Settings → Features), customers automatically earn <strong>1 point per $1</strong> of order subtotal, tied to their phone number. <strong>100 points = $1 off</strong>. Points are shared across all your locations. You can view balances and make manual adjustments in the <strong>Loyalty tab</strong>. Self-service redemption at checkout is on the roadmap — for now, staff apply redemptions from there.</p>,
      },
      {
        q: "What are feature toggles?",
        a: <p>Settings → Feature toggles lets you enable or disable the fulfillment queue, loyalty program, and AI phone agent per your business type. Changes apply immediately — no restart needed.</p>,
      },
      {
        q: "What is SMS marketing and how is it billed?",
        a: (
          <>
            <p>SMS marketing lets you send a text message to every customer who has ever ordered from you — directly from your AI phone number. Enable it in the <strong>Customers tab</strong> using the SMS marketing toggle.</p>
            <p><strong>Billing:</strong> The add-on costs <strong>$9/month</strong> and appears on your next invoice. Importantly, the charge only applies the first month you actually send a message. If you enable the toggle but never send a blast, you won't be billed.</p>
            <p><strong>Disabling:</strong> Turning the toggle off takes effect at the end of your current billing cycle. You can still send messages until then — the feature doesn't cut off mid-cycle.</p>
            <p>SMS marketing requires a provisioned AI phone number (set up in the AI Host tab). Messages are sent from that number and are limited to 160 characters.</p>
          </>
        ),
      },
      {
        q: "Does Ordrs support multiple locations?",
        a: <p>Yes, on the <strong>Enterprise plan</strong>. Each location has its own address, phone number, hours, tax rate, and printer config. All locations are managed from a single dashboard. Per-location staff logins are planned for a future release.</p>,
      },
      {
        q: "What is booking mode and how do reservations vs appointments work?",
        a: (
          <>
            <p>Booking mode controls whether your <strong>Appointments</strong> tab (and related language throughout the dashboard) is set up for reservations or appointments — or turned off entirely.</p>
            <p><strong>Reservations</strong> — for restaurants and dining venues. The tab uses dining-centric language: "Dining Options" instead of Services, party size and date/time collection, and reservation-appropriate empty states.</p>
            <p><strong>Appointments</strong> — for service businesses like salons, clinics, and studios. The tab shows service types with durations and pricing, staff scheduling, and appointment confirmation flows.</p>
            <p><strong>Off</strong> — hides the booking feature entirely. Use this if you only take orders and don't need scheduling.</p>
            <p>Change your booking mode in <strong>Settings → Booking mode</strong>. The interface updates immediately.</p>
          </>
        ),
      },
      {
        q: "How do customers book an appointment or reservation?",
        a: (
          <>
            <p>Two ways today: callers can request a time through your <strong>AI phone host</strong> (set the persona to Booking Host or Concierge), and you or your staff can create appointments directly in the <strong>Appointments tab</strong> — pick the service, staff member, and an open slot.</p>
            <p>Phone requests arrive in your dashboard with the caller's details and requested time; confirm them against your calendar from there. Self-service booking on the storefront itself is on the roadmap.</p>
          </>
        ),
      },
      {
        q: "Can my staff have their own logins?",
        a: <p>Yes. Invite teammates by email from your dashboard and assign a role — <strong>manager</strong> or <strong>staff</strong>. Each role sees only what it needs; owners keep full control over billing, performance, and settings.</p>,
      },
      {
        q: "Can orders print to a kitchen printer?",
        a: <p>Yes. Ordrs integrates with <strong>PrintNode</strong> — connect a supported thermal printer and paid orders print a kitchen ticket automatically. You can reprint any order from the dashboard, and view print job history per order.</p>,
      },
      {
        q: "Do my customers get accounts?",
        a: <p>Optionally. Customers can check out as guests, or create an account on your storefront to see their order history, save delivery addresses, and track loyalty points. Accounts are per-storefront — your customer list stays yours.</p>,
      },
    ],
  },
  {
    title: "AI Advisor",
    items: [
      {
        q: "What is the AI Advisor?",
        a: (
          <>
            <p>The AI Advisor is a dashboard panel on desktop and a card on mobile. It shows short operational prompts based on your dashboard state and lets you open a chat for help.</p>
            <p>It does NOT click buttons, fill forms, or make changes. Treat its guidance as a review aid, not a decision-maker.</p>
          </>
        ),
      },
      {
        q: "What kind of tips does it give?",
        a: (
          <>
            <p>Tips are specific to the page you're on:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong>Dashboard</strong> — order volume alerts, menu gaps, customer engagement ideas, AI phone host tips</li>
              <li><strong>Kitchen</strong> — order flow bottlenecks, timing warnings, queue depth, special instructions</li>
              <li><strong>Build</strong> — design polish, menu completeness, SEO, missing features like online ordering or booking</li>
              <li><strong>Phone Sim</strong> — test quality, menu clarity for callers, greeting optimization, transcript review</li>
              <li><strong>Menu</strong> — pricing psychology, description quality, availability hygiene, category organization</li>
              <li><strong>Onboarding</strong> — step-by-step setup guidance, menu import, phone connection, first test order</li>
            </ul>
          </>
        ),
      },
      {
        q: "Does the AI Advisor know about my specific business?",
        a: (
          <>
            <p>The dashboard prompts use the business state already loaded into your dashboard, such as pending work, configured services or menu items, phone-host status, and recent call counts. The advisor chat receives the context needed to answer your question.</p>
            <p>It does not automatically take actions or send messages. Verify a recommendation before changing operations or communicating with customers.</p>
          </>
        ),
      },
      {
        q: "How does it decide what to suggest?",
        a: (
          <>
            <p>The dashboard advisor:</p>
            <ol className="list-decimal pl-5 space-y-1 mt-2">
              <li><strong>Checks</strong> — evaluates the operational summary already loaded in the dashboard</li>
              <li><strong>Highlights</strong> — surfaces actionable prompts, such as pending work or missing setup</li>
              <li><strong>Answers</strong> — sends a chat question to the server-side assistant when you ask one</li>
              <li><strong>Displays</strong> — rotates available prompts in the advisor panel or mobile card</li>
            </ol>
            <p className="mt-2">The dashboard does not require a browser-side AI key to show operational prompts.</p>
          </>
        ),
      },
      {
        q: "Can I pause or dismiss the ticker?",
        a: <p>On desktop, minimize the advisor with the arrow in its header. On mobile, open the advisor card when you want to chat. You can move to the next prompt from either surface.</p>,
      },
      {
        q: "Is my data sent to the AI?",
        a: (
          <>
            <p>Dashboard prompts are computed locally from the operational summary already displayed in your dashboard. They do not need a browser-side model key.</p>
            <p>When you use advisor chat, your question and relevant business context are sent to the server-side assistant. Do not include sensitive customer information in a chat question, and review your provider and internal data-handling policies before using AI guidance.</p>
          </>
        ),
      },
      {
        q: "Can I turn the AI Advisor off?",
        a: <p>You can minimize it on desktop or simply leave the mobile advisor card closed. A dedicated Settings toggle is planned.</p>,
      },
    ],
  },
];

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-0">
      <button
        className="w-full text-left py-4 flex items-start justify-between gap-4 group"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="font-medium text-foreground group-hover:text-primary transition-colors leading-snug">{q}</span>
        <span className="shrink-0 mt-0.5 text-muted-foreground text-lg leading-none select-none">
          {open ? "−" : "+"}
        </span>
      </button>
      {open && (
        <div className="pb-5 text-muted-foreground leading-relaxed space-y-3 [&_a]:text-primary [&_a]:hover:underline [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_strong]:text-foreground">
          {a}
        </div>
      )}
    </div>
  );
}

export default function FAQ() {
  return (
    <LegalLayout title="Help & FAQ" updated={null}>
      <div className="legal-callout">
        Can't find what you're looking for? Email us at{" "}
        <a href="mailto:support@ordrs.ai">support@ordrs.ai</a> — we typically respond within one business day.
      </div>

      {SECTIONS.map((section) => (
        <section key={section.title} className="mb-10">
          <h2>{section.title}</h2>
          <div className="border-t border-border">
            {section.items.map((item) => (
              <FAQItem key={item.q} q={item.q} a={item.a} />
            ))}
          </div>
        </section>
      ))}

      <div className="mt-12 pt-8 border-t border-border text-center">
        <p className="text-muted-foreground mb-1">Still have a question?</p>
        <a href="mailto:support@ordrs.ai" className="text-primary font-medium hover:underline">
          support@ordrs.ai
        </a>
        <p className="text-sm text-muted-foreground mt-4">
          See also: <Link to="/pricing" className="hover:text-primary transition-colors">Pricing</Link>{" · "}
          <Link to="/refund" className="hover:text-primary transition-colors">Refund Policy</Link>{" · "}
          <Link to="/terms" className="hover:text-primary transition-colors">Terms of Service</Link>
        </p>
      </div>
    </LegalLayout>
  );
}
